<template>
    <div :id="anchorsPageProfile.navigationSection" class="navigation-tabs">
        <span class="navigation-tabs__go-to">{{ $t('profile.view.navigation.tabs.go.to') }}</span>
        <nav class="navigation-tabs__nav">
            <ul class="navigation-tabs__nav-list">
                <li v-for="tab in tabs.filter((tab) => tab.visible)" :key="tab.label">
                    <VJoyButton class="navigation-tabs__nav-item" :icon="tab.icon" :href="tab.href" size="xxsmall" @click="tab.onClick">
                        {{ tab.label }}
                    </VJoyButton>
                </li>
            </ul>
        </nav>
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import type {JoyIconsId} from '@maltjoy/icons';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import {anchorsPageProfile} from '~/config/anchors';
    import {ProfileSectionReachedSection, ProfileSectionReachedEntryPoint, ProfileSectionReachedContext} from '~/config/TrackingEvents';
    import {useProfileStore} from '~/store/profileStore';
    import {useSectionsVisibilityStore} from '~/store/sectionsVisibility';
    import {SectionsVisibilityType} from '~/types/domain/SectionsVisibility';

    const {t} = useTranslation();
    const {trackProfileSectionReached} = useProfileTracking();
    const {profile} = storeToRefs(useProfileStore());
    const {sectionVisibility} = storeToRefs(useSectionsVisibilityStore());

    interface Tab {
        visible: boolean;
        label: string;
        href: string;
        icon: JoyIconsId;
        onClick: () => void;
    }

    const tabs = computed<Tab[]>(() => [
        {
            visible: true,
            label: t('profile.view.navigation.tabs.about'),
            href: `#${anchorsPageProfile.aboutSection}`,
            icon: 'file',
            onClick: () => trackProfileSectionReachedFromNavigationBar(ProfileSectionReachedSection.ABOUT),
        },
        {
            visible: sectionVisibility.value[SectionsVisibilityType.EXPERIENCES],
            label: t('profile.view.navigation.tabs.experiences'),
            href: `#${anchorsPageProfile.experiencesSection}`,
            icon: 'briefcase',
            onClick: () => trackProfileSectionReachedFromNavigationBar(ProfileSectionReachedSection.EXPERIENCE),
        },
        {
            visible: sectionVisibility.value[SectionsVisibilityType.PORTFOLIO] || sectionVisibility.value[SectionsVisibilityType.PORTFOLIO_REVAMP],
            label: t('profile.view.navigation.tabs.portfolio'),
            href: `#${anchorsPageProfile.portfolioSection}`,
            icon: 'color',
            onClick: () => trackProfileSectionReachedFromNavigationBar(ProfileSectionReachedSection.PORTFOLIO),
        },
        {
            visible: sectionVisibility.value[SectionsVisibilityType.APPRAISALS],
            label: t('profile.view.navigation.tabs.reviews'),
            href: `#${anchorsPageProfile.appraisalsSection}`,
            icon: 'star',
            onClick: () => trackProfileSectionReachedFromNavigationBar(ProfileSectionReachedSection.REVIEWS),
        },
        {
            visible: sectionVisibility.value[SectionsVisibilityType.RECOMMENDATIONS],
            label: t('profile.view.navigation.tabs.recommendations'),
            href: `#${anchorsPageProfile.recommendationsSection}`,
            icon: 'thumbup',
            onClick: () => trackProfileSectionReachedFromNavigationBar(ProfileSectionReachedSection.RECOMMENDATIONS),
        },
        {
            visible: sectionVisibility.value[SectionsVisibilityType.EDUCATIONS],
            label: t('profile.view.navigation.tabs.education'),
            href: `#${anchorsPageProfile.educationSection}`,
            icon: 'school',
            onClick: () => trackProfileSectionReachedFromNavigationBar(ProfileSectionReachedSection.EDUCATION),
        },
    ]);

    function trackProfileSectionReachedFromNavigationBar(section: ProfileSectionReachedSection) {
        trackProfileSectionReached(
            section,
            ProfileSectionReachedEntryPoint.PROFILE_NAVIGATION_BAR,
            profile.value?.profileId,
            profile.value?.accountId,
            ProfileSectionReachedContext.PROFILE_PAGE,
        );
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .navigation-tabs {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: var(--joy-core-spacing-8);
        font-size: var(--joy-font-size-primary-300);

        &__go-to {
            white-space: nowrap;
            margin-right: var(--joy-core-spacing-5);

            @include mq.screen_sm(max) {
                display: none;
            }
        }

        &__nav {
            max-width: 100%;

            &-list {
                display: flex;
                gap: var(--joy-core-spacing-4);
                overflow-x: auto;
                -webkit-overflow-scrolling: touch;
                overscroll-behavior-x: contain;
                scrollbar-width: none;
                -ms-overflow-style: none;
                &::-webkit-scrollbar {
                    display: none;
                }

                list-style: none;
                padding: 0;
                margin: 0;

                li {
                    flex: 0 0 auto;
                }
            }
        }
    }
</style>
